import { HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { tap } from 'rxjs';

/** The key for the basket open header */
export const OCS_BASKET_OPEN_HEADER_KEY = 'gs.ocs.basket.open';

/** Cookie key that needs to be forwarded as a header */
const CHANNEL_COOKIE_KEY = 'channel';

/**
 * Cookie key marking a request that runs inside an app WebView. The app writes it from the page dialog's
 * headers, the backend reads it back to decide whether the page still gets the website shell. Deliberately
 * separate from the channel: that one names the sitemap resolving the url, and for a regular CS4 page it is
 * the CS4 channel — from the channel alone a WebView is indistinguishable from a browser visit (CS4-2372).
 * Forwarded here for the browser and for the Angular SSR render, which runs through this interceptor too.
 * The separate fetch in server.ts deliberately does not send it: it only pulls SEO metadata, which this
 * flag does not affect.
 */
export const IN_APP_COOKIE_KEY = 'inapp';

/**
 * Holds the basket open value for subsequent requests. A root service instead of a module constant: on the
 * server every request renders its own app, while a module constant would be shared by all visitors.
 */
@Injectable({ providedIn: 'root' })
export class BasketOpenState {
  /** The basket open value of the last response that carried one */
  readonly value = signal<string | null>(null);
}

/**
 * Plugin Interceptor
 * Applies to all HTTP requests.
 *
 * Outgoing requests:
 * - Forwards token, channel and in-app cookies as headers (may be set by backend or external systems)
 * - Attaches basket open status to all requests
 *
 * Incoming responses:
 * - Reads and stores basket open status from responses
 * - Stored values are then attached to all subsequent requests
 *
 * Note: gs.ocs.* cookie forwarding is handled by authInterceptor.
 */
export const pluginInterceptor: HttpInterceptorFn = (req, next) => {
  const basketOpenState = inject(BasketOpenState);
  const cookieService = inject(SsrCookieService);
  const headers: Record<string, string> = {};

  // Read gs.ocs.basket.open from cookie first, then fall back to stored signal value
  const basketOpenCookie = cookieService.get(OCS_BASKET_OPEN_HEADER_KEY);
  const currentBasketOpen = basketOpenCookie || basketOpenState.value();

  if (currentBasketOpen) {
    headers[OCS_BASKET_OPEN_HEADER_KEY] = currentBasketOpen;
  }

  const channelCookie = cookieService.get(CHANNEL_COOKIE_KEY);
  if (channelCookie) {
    headers[CHANNEL_COOKIE_KEY] = channelCookie;
  }

  const inAppCookie = cookieService.get(IN_APP_COOKIE_KEY);
  if (inAppCookie) {
    headers[IN_APP_COOKIE_KEY] = inAppCookie;
  }

  if (Object.keys(headers).length > 0) {
    req = req.clone({
      setHeaders: headers
    });
  }

  return next(req).pipe(
    tap((res) => {
      if (!res || res.type === HttpEventType.Sent || !('headers' in res)) return;

      const basketOpen = res.headers.get(OCS_BASKET_OPEN_HEADER_KEY);
      if (basketOpen) {
        basketOpenState.value.set(basketOpen);
      }
    })
  );
};
